import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import './Dashboard.css';

function Dashboard({ contract, userId, account }) {
  const [userData, setUserData] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [pendingIncomes, setPendingIncomes] = useState(null);
  const [binaryInfo, setBinaryInfo] = useState(null);
  const [sponsorInfo, setSponsorInfo] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [treeData, setTreeData] = useState(null);
  const [treeRootId, setTreeRootId] = useState(0);
  const [searchTreeId, setSearchTreeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState('');

  const loadUserData = async () => {
    if (!contract || userId === 0) {
      setLoading(false);
      return;
    }

    try {
      const [basic, earn, binary, sponsor, pendingBinary, pendingReferral] = await Promise.all([
        contract.getUserBasic(userId),
        contract.getEarningsBreakdown(userId),
        contract.getBinaryInfo(userId),
        contract.getSponsorInfo(userId),
        contract.getTotalPendingBinaryIncome(userId),
        contract.getTotalPendingReferralIncome(userId)
      ]);

      setUserData({
        wallet: basic.wallet,
        totalInvestment: ethers.utils.formatEther(basic.totalInvestment),
        currentPackage: ethers.utils.formatEther(basic.currentPackage),
        availableBalance: ethers.utils.formatEther(basic.availableBalance),
        active: basic.active
      });

      setEarnings({
        roiEarned: ethers.utils.formatEther(earn.roiEarned),
        binaryEarned: ethers.utils.formatEther(earn.binaryEarned),
        referralEarned: ethers.utils.formatEther(earn.referralEarned),
        totalEarned: ethers.utils.formatEther(earn.totalEarned),
        totalWithdrawn: ethers.utils.formatEther(earn.totalWithdrawn),
        availableBalance: ethers.utils.formatEther(earn.availableBalance)
      });

      setPendingIncomes({
        binaryTotal: ethers.utils.formatEther(pendingBinary),
        referralTotal: ethers.utils.formatEther(pendingReferral)
      });

      setBinaryInfo({
        leftBusiness: ethers.utils.formatEther(binary.leftBusiness),
        rightBusiness: ethers.utils.formatEther(binary.rightBusiness),
        leftBusinessTotal: ethers.utils.formatEther(binary.leftBusinessTotal),
        rightBusinessTotal: ethers.utils.formatEther(binary.rightBusinessTotal),
        hasBinaryEligibility: binary.hasBinaryEligibility
      });

      setSponsorInfo({
        sponsorId: sponsor.sponsorId.toNumber(),
        referrerId: sponsor.referrerId.toNumber(),
        directReferrals: sponsor.directReferrals.toNumber()
      });

      // Load investment details
      const invCount = await contract.getInvCount(userId);
      const invs = [];
      for (let i = 0; i < invCount.toNumber(); i++) {
        const inv = await contract.getInvestmentDetails(userId, i);
        invs.push({
          amount: parseFloat(ethers.utils.formatEther(inv.amount)),
          timestamp: inv.timestamp.toNumber(),
          roiEarned: parseFloat(ethers.utils.formatEther(inv.roiEarned)),
          daysActive: inv.daysActive.toNumber(),
          active: inv.active,
          type: inv.investmentType
        });
      }
      setInvestments(invs);

      setTreeRootId(userId);
      await loadTreeData(userId);

    } catch (err) {
      console.error('Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTreeData = async (rootId) => {
    if (!contract || rootId === 0) return;

    try {
      const tree = await contract.getTree(rootId);
      const leftChild = tree.leftChild.toNumber();
      const rightChild = tree.rightChild.toNumber();

      let leftData = null;
      let rightData = null;

      if (leftChild !== 0) {
        const leftTree = await contract.getTree(leftChild);
        const leftBasic = await contract.getUserBasic(leftChild);
        leftData = {
          id: leftChild,
          investment: ethers.utils.formatEther(leftBasic.totalInvestment),
          active: leftBasic.active,
          leftChild: leftTree.leftChild.toNumber(),
          rightChild: leftTree.rightChild.toNumber()
        };
      }

      if (rightChild !== 0) {
        const rightTree = await contract.getTree(rightChild);
        const rightBasic = await contract.getUserBasic(rightChild);
        rightData = {
          id: rightChild,
          investment: ethers.utils.formatEther(rightBasic.totalInvestment),
          active: rightBasic.active,
          leftChild: rightTree.leftChild.toNumber(),
          rightChild: rightTree.rightChild.toNumber()
        };
      }

      const rootBasic = await contract.getUserBasic(rootId);

      setTreeData({
        root: {
          id: rootId,
          investment: ethers.utils.formatEther(rootBasic.totalInvestment),
          active: rootBasic.active
        },
        left: leftData,
        right: rightData
      });
    } catch (err) {
      console.error('Error loading tree:', err);
    }
  };

  const handleTreeSearch = async () => {
    const id = parseInt(searchTreeId);
    if (id > 0) {
      setTreeRootId(id);
      await loadTreeData(id);
    }
  };

  const handleNodeClick = async (nodeId) => {
    if (nodeId > 0) {
      setTreeRootId(nodeId);
      setSearchTreeId(nodeId.toString());
      await loadTreeData(nodeId);
    }
  };

  const copyReferralLink = (position) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}?ref=${userId}&pos=${position}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(position);
    setTimeout(() => setCopiedLink(''), 2000);
  };

  const calculateCeilingProgress = () => {
    if (!userData || !earnings) return { percent: 0, current: 0, max: 0 };
    const totalInv = parseFloat(userData.totalInvestment);
    const totalEarned = parseFloat(earnings.totalEarned);
    const maxCap = totalInv * 3; // 300% cap
    const percent = maxCap > 0 ? Math.min((totalEarned / maxCap) * 100, 100) : 0;
    return { percent, current: totalEarned, max: maxCap };
  };

  const calculateROIProgress = () => {
    if (!userData || !earnings) return { percent: 0, current: 0, max: 0 };
    const totalInv = parseFloat(userData.totalInvestment);
    const roiEarned = parseFloat(earnings.roiEarned);
    const maxROI = totalInv * 2; // 200% ROI cap
    const percent = maxROI > 0 ? Math.min((roiEarned / maxROI) * 100, 100) : 0;
    return { percent, current: roiEarned, max: maxROI };
  };

  useEffect(() => {
    loadUserData();
    const interval = setInterval(loadUserData, 30000);
    return () => clearInterval(interval);
  }, [contract, userId]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-orb">
          <div className="orb-inner"></div>
        </div>
        <p>Initializing Neural Network...</p>
      </div>
    );
  }

  if (userId === 0) {
    return (
      <div className="not-registered">
        <div className="scan-line"></div>
        <div className="not-registered-icon">🔐</div>
        <h2>Register Below to get started</h2>
        <p>Register and invest to unlock the AI trading dashboard</p>
      </div>
    );
  }

  const ceilingProgress = calculateCeilingProgress();
  const roiProgress = calculateROIProgress();

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>
          <div className="ai-indicator">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
          AI Trading Dashboard
        </h2>
        <div className="user-badge">
          <span className="badge-label">User ID</span>
          <span className="badge-value">{userId}</span>
        </div>
      </div>

      <div className="referral-links">
        <div className="ref-link-card left">
          <div className="ref-icon">⬅️</div>
          <div className="ref-info">
            <span className="ref-label">Left Referral Link</span>
            <span className="ref-url">{window.location.origin}?ref={userId}&pos=left</span>
          </div>
          <button 
            className={`copy-btn ${copiedLink === 'left' ? 'copied' : ''}`}
            onClick={() => copyReferralLink('left')}
          >
            {copiedLink === 'left' ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <div className="ref-link-card right">
          <div className="ref-icon">➡️</div>
          <div className="ref-info">
            <span className="ref-label">Right Referral Link</span>
            <span className="ref-url">{window.location.origin}?ref={userId}&pos=right</span>
          </div>
          <button 
            className={`copy-btn ${copiedLink === 'right' ? 'copied' : ''}`}
            onClick={() => copyReferralLink('right')}
          >
            {copiedLink === 'right' ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card primary glow">
          <div className="stat-decoration"></div>
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <span className="stat-label">Available Balance:  </span>
            <span className="stat-value">${parseFloat(earnings?.availableBalance || 0).toFixed(2)}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <span className="stat-label">Total Investment:  </span>
            <span className="stat-value">${parseFloat(userData?.totalInvestment || 0).toFixed(0)}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <span className="stat-label">Total Earned:  </span>
            <span className="stat-value">${parseFloat(earnings?.totalEarned || 0).toFixed(2)}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💸</div>
          <div className="stat-content">
            <span className="stat-label">Total Withdrawn:  </span>
            <span className="stat-value">${parseFloat(earnings?.totalWithdrawn || 0).toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="progress-section">
        <div className="progress-card ceiling">
          <div className="progress-header">
            <h4>🎯 300% Ceiling Progress</h4>
            <span className="progress-values">
              ${ceilingProgress.current.toFixed(2)} / ${ceilingProgress.max.toFixed(0)}
            </span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar ceiling"
              style={{ width: `${ceilingProgress.percent}%` }}
            >
              <span className="progress-percent">{ceilingProgress.percent.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        <div className="progress-card roi">
          <div className="progress-header">
            <h4>📈 ROI Progress (200% Cap)</h4>
            <span className="progress-values">
              ${roiProgress.current.toFixed(2)} / ${roiProgress.max.toFixed(0)}
            </span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar roi"
              style={{ width: `${roiProgress.percent}%` }}
            >
              <span className="progress-percent">{roiProgress.percent.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="income-cards">
        <div className="income-card binary">
          <div className="income-header">
            <h4>🌐 Binary Income</h4>
            <span className="income-badge">10% Matching</span>
          </div>
          <div className="income-amount">
            <span className="total-label">Total Earned</span>
            <span className="total">${parseFloat(pendingIncomes?.binaryTotal || 0).toFixed(2)}</span>
          </div>
          <div className="income-amount">
            <span className="released">${parseFloat(earnings?.binaryEarned || 0).toFixed(2)}</span>
            <span className="label">Released (50-day split)</span>
          </div>
          <div className="income-progress">
            <div className="progress-bar-mini">
              <div 
                className="progress-fill binary"
                style={{ 
                  width: `${pendingIncomes?.binaryTotal > 0 
                    ? Math.min((parseFloat(earnings?.binaryEarned || 0) / parseFloat(pendingIncomes?.binaryTotal)) * 100, 100) 
                    : 0}%` 
                }}
              ></div>
            </div>
            <span className="progress-label">
              {pendingIncomes?.binaryTotal > 0 
                ? ((parseFloat(earnings?.binaryEarned || 0) / parseFloat(pendingIncomes?.binaryTotal)) * 100).toFixed(1)
                : 0}% Released
            </span>
          </div>
          <div className="income-note">
            Income releases 2% daily over 50 days
          </div>
        </div>

        <div className="income-card referral">
          <div className="income-header">
            <h4>👥 Referral Income</h4>
            <span className="income-badge">10% Direct</span>
          </div>
          <div className="income-amount">
            <span className="total-label">Total Earned</span>
            <span className="total">${parseFloat(pendingIncomes?.referralTotal || 0).toFixed(2)}</span>
          </div>
          <div className="income-amount">
            <span className="released">${parseFloat(earnings?.referralEarned || 0).toFixed(2)}</span>
            <span className="label">Released (50-day split)</span>
          </div>
          <div className="income-progress">
            <div className="progress-bar-mini">
              <div 
                className="progress-fill referral"
                style={{ 
                  width: `${pendingIncomes?.referralTotal > 0 
                    ? Math.min((parseFloat(earnings?.referralEarned || 0) / parseFloat(pendingIncomes?.referralTotal)) * 100, 100) 
                    : 0}%` 
                }}
              ></div>
            </div>
            <span className="progress-label">
              {pendingIncomes?.referralTotal > 0 
                ? ((parseFloat(earnings?.referralEarned || 0) / parseFloat(pendingIncomes?.referralTotal)) * 100).toFixed(1)
                : 0}% Released
            </span>
          </div>
          <div className="income-note">
            Income releases 2% daily over 50 days
          </div>
        </div>
      </div>

      <div className="info-grid">
        <div className="info-card glass">
          <div className="card-header">
            <h3>🌐 Network Metrics</h3>
          </div>
          <div className="binary-visual">
            <div className="leg left-leg">
              <div className="leg-label">LEFT</div>
              <div className="leg-value">${parseFloat(binaryInfo?.leftBusiness || 0).toFixed(0)}</div>
              <div className="leg-total">Total: ${parseFloat(binaryInfo?.leftBusinessTotal || 0).toFixed(0)}</div>
            </div>
            <div className="leg right-leg">
              <div className="leg-label">RIGHT</div>
              <div className="leg-value">${parseFloat(binaryInfo?.rightBusiness || 0).toFixed(0)}</div>
              <div className="leg-total">Total: ${parseFloat(binaryInfo?.rightBusinessTotal || 0).toFixed(0)}</div>
            </div>
          </div>
          <div className="info-row">
            <span>Binary Status</span>
            <span className={`status-badge ${binaryInfo?.hasBinaryEligibility ? 'active' : 'inactive'}`}>
              {binaryInfo?.hasBinaryEligibility ? '● ACTIVE' : '○ INACTIVE'}
            </span>
          </div>
        </div>

        <div className="info-card glass">
          <div className="card-header">
            <h3>👥 Team Statistics</h3>
          </div>
          <div className="info-row">
            <span>Direct Referrals</span>
            <span className="value highlight">{sponsorInfo?.directReferrals || 0}</span>
          </div>
          <div className="info-row">
            <span>Sponsor ID</span>
            <span className="value">{sponsorInfo?.sponsorId || 0}</span>
          </div>
          <div className="info-row">
            <span>Upline ID</span>
            <span className="value">{sponsorInfo?.referrerId || 0}</span>
          </div>
          <div className="info-row">
            <span>Current Package</span>
            <span className="value">${parseFloat(userData?.currentPackage || 0).toFixed(0)}</span>
          </div>
        </div>
      </div>

      {/* Investment ROI Details */}
      {investments.length > 0 && (
        <div className="investments-roi-section">
          <h3>📈 Investment ROI Tracking</h3>
          <div className="investments-roi-grid">
            {investments.map((inv, idx) => {
              const maxROI = inv.amount * 2; // 200% ROI cap per investment
              const roiPercent = maxROI > 0 ? Math.min((inv.roiEarned / maxROI) * 100, 100) : 0;
              const dailyRate = inv.type !== 2 ? (binaryInfo?.hasBinaryEligibility ? 1 : 0.5) : 0;
              
              return (
                <div key={idx} className={`investment-roi-card ${inv.active ? 'active' : 'completed'}`}>
                  <div className="inv-roi-header">
                    <span className="inv-amount">${inv.amount.toFixed(0)}</span>
                    <span className={`inv-status ${inv.active ? 'active' : 'completed'}`}>
                      {inv.active ? 'Active' : 'Completed'}
                    </span>
                  </div>
                  <div className="inv-roi-info">
                    <span>Day {inv.daysActive} • {dailyRate}% daily</span>
                    <span>${inv.roiEarned.toFixed(2)} / ${maxROI.toFixed(0)}</span>
                  </div>
                  <div className="inv-progress-bar">
                    <div 
                      className="inv-progress-fill"
                      style={{ width: `${roiPercent}%` }}
                    ></div>
                  </div>
                  <div className="inv-roi-percent">{roiPercent.toFixed(1)}% ROI</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="tree-section glass">
        <div className="tree-header">
          <h3>🌳 Binary Network Visualization</h3>
          <div className="tree-controls">
            <input
              type="number"
              placeholder="User ID"
              value={searchTreeId}
              onChange={(e) => setSearchTreeId(e.target.value)}
            />
            <button onClick={handleTreeSearch}>Search</button>
            <button onClick={() => handleNodeClick(userId)} className="reset-btn">
              Reset
            </button>
          </div>
        </div>

        {treeData && (
          <div className="tree-container">
            <div className="tree-level level-0">
              <div 
                className={`tree-node root ${treeData.root.active ? 'active' : 'inactive'}`}
                onClick={() => handleNodeClick(treeData.root.id)}
              >
                <div className="node-glow"></div>
                <div className="node-id">#{treeData.root.id}</div>
                <div className="node-investment">${parseFloat(treeData.root.investment).toFixed(0)}</div>
              </div>
            </div>

            <div className="tree-lines">
              <svg width="100%" height="60">
                <line x1="50%" y1="0" x2="25%" y2="60" stroke="rgba(59, 130, 246, 0.4)" strokeWidth="2"/>
                <line x1="50%" y1="0" x2="75%" y2="60" stroke="rgba(147, 51, 234, 0.4)" strokeWidth="2"/>
              </svg>
            </div>

            <div className="tree-level level-1">
              <div className="tree-side left-side">
                {treeData.left ? (
                  <div 
                    className={`tree-node left-node ${treeData.left.active ? 'active' : 'inactive'}`}
                    onClick={() => handleNodeClick(treeData.left.id)}
                  >
                    <div className="node-id">#{treeData.left.id}</div>
                    <div className="node-investment">${parseFloat(treeData.left.investment).toFixed(0)}</div>
                    <div className="node-children">
                      <span>L:{treeData.left.leftChild || '—'}</span>
                      <span>R:{treeData.left.rightChild || '—'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="tree-node empty">
                    <div className="node-id">Empty</div>
                    <div className="node-investment">$0</div>
                  </div>
                )}
              </div>

              <div className="tree-side right-side">
                {treeData.right ? (
                  <div 
                    className={`tree-node right-node ${treeData.right.active ? 'active' : 'inactive'}`}
                    onClick={() => handleNodeClick(treeData.right.id)}
                  >
                    <div className="node-id">#{treeData.right.id}</div>
                    <div className="node-investment">${parseFloat(treeData.right.investment).toFixed(0)}</div>
                    <div className="node-children">
                      <span>L:{treeData.right.leftChild || '—'}</span>
                      <span>R:{treeData.right.rightChild || '—'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="tree-node empty">
                    <div className="node-id">Empty</div>
                    <div className="node-investment">$0</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;